import {ApiClient, type ErrorResponse} from '@hconnect/apiclient'
import {useUpmPlantContext, type UpmPlant} from '@hconnect/upm'
import {spawnErrorToast, spawnSuccessToast} from '@hm/fundamental'
import {useMutation, useQueryClient} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useCallback} from 'react'
import {useErrorHandler} from 'react-error-boundary'
import {useTranslation} from 'react-i18next'

import {
  FEATURE_NAMESPACE,
  OPTIMIZER_RUNS_AND_SCHEDULES_QUERY_KEY,
  OPTIMIZER_SETTINGS_QUERY_KEY,
  getOptimizerRunsPath
} from '../consts'
import {mapOptimizerSettingsToDTO} from '../selectors'
import {RecalculatePlanData} from '../types'
import {notifyIfErrorMessage} from '../utils'

export const useRecalculatePlan = () => {
  const {plantId: plantCode}: UpmPlant = useUpmPlantContext()
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const raiseError = useErrorHandler()
  const queryClient = useQueryClient()

  const {mutate, isPending: isLoading} = useMutation<
    void,
    AxiosError<ErrorResponse>,
    RecalculatePlanData
  >({
    mutationFn: async (data) => {
      await ApiClient.axiosInstance.post<void>(getOptimizerRunsPath(plantCode), {
        settings: mapOptimizerSettingsToDTO(data.optimizerSettings, true),
        save_settings: data.saveSettings,
        name: data.name,
        forecast_type: data.forecastType
      })
    },
    onError: (error) => {
      spawnErrorToast(t('error.apology'))
      notifyIfErrorMessage(error.response?.data.detail, raiseError, spawnErrorToast)
    },
    onSuccess: () => {
      spawnSuccessToast(t('success.recalculateOptimizedPlanning'))
    },
    onSettled: (data, error, variables) => {
      if (variables.saveSettings) {
        void queryClient.invalidateQueries({
          queryKey: [OPTIMIZER_SETTINGS_QUERY_KEY, {plantCode}],
          refetchType: 'all'
        })
      }
      void queryClient.invalidateQueries({
        queryKey: [OPTIMIZER_RUNS_AND_SCHEDULES_QUERY_KEY, {plantCode}],
        refetchType: 'all'
      })
    }
  })

  const recalculatePlan = useCallback((data: RecalculatePlanData) => mutate(data), [mutate])

  return {recalculatePlan, isLoading}
}
